import getDistance from 'geolib/es/getDistance'
import { toHiragana, toKatakana } from './utils'
import api from '../../api'
import _ from 'lodash'
import Constants from '../../constants'

const getDefaultState = () => {
  return {
    // TODO: 緯度経度の持ち方として、latitudeとlongitudeとに統一した方がよさそう
    center: {
      lat: Constants.DEFAULT_STATION_LOCATION.latitude,
      lng: Constants.DEFAULT_STATION_LOCATION.longitude
    },
    offset: false,
    zoom: 15,
    from: {},
    durations: [],
    googlePlaceMarker: {},
    height: 0,
    searchFrom: {},
    searchTo: {},
    spotMarkerList: []
  }
}

const state = getDefaultState()

const getters = {
  getCenter: (state) => state.center,
  getOffset: (state) => state.offset,
  getZoom: (state) => state.zoom,
  getFrom: (state) => state.from,
  getSearchFrom: (state) => state.searchFrom,
  getSearchTo: (state) => state.searchTo,
  getDurations: (state) => state.durations.sort((a, b) => a.index - b.index),
  search: (state, _getters, _rootState, rootGetters) => (word) => {
    const places = _.cloneDeep(rootGetters['places/getPlaces'])

    return places
      .filter(place => isMatch(place, word))
      .map(place => {
        return Object.assign(place, { distance: Math.round(getDistance(place.location, state.center) * 10) / 100 })
      })
      .sort((a, b) => {
        return a.distance - b.distance
      })
  },
  getSelectedPlaceDuration: (state) => state.selectedPlaceDuration,
  getGooglePlaceMarker: (state) => state.googlePlaceMarker,
  getRecommendedPlaces: (state, _getters, _rootState, rootGetters) => {
    const places = _.cloneDeep(rootGetters['places/getPlaces'])
    const fromLocation = assignFromLocation(state.route, state.center)

    return places
      .filter(place => {
        return place.image !== undefined ? true : false
      })
      .map(place => {
        return Object.assign(place, { distance: getDistance(place.location, fromLocation) })
      })
      .sort((a, b) => {
        return a.distance - b.distance
      })
      .slice(0, 5)
  },
  getFavoritedPlaces: (state, _getters, _rootState, rootGetters) => {
    const places = _.cloneDeep(rootGetters['places/getPlaces'])
    const favorites = rootGetters['favorites/getFavorites']
    const fromLocation = assignFromLocation(state.route, state.center)

    return places
      .filter(place => {
        return favorites.includes(place.id)
      })
      .map(place => {
        return Object.assign(place, { distance: getDistance(place.location, fromLocation) })
      })
      .sort((a, b) => {
        return a.distance - b.distance
      })
      .slice(0, 5)
  },
  getSpotMarkerList: (state) => state.spotMarkerList,
  getHeight: (state)  => state.height
}

const actions = {
  updateCenter ({ commit }, params) {
    commit('setCenter', params)
  },
  updateCenterLatLng ({ commit }, params) {
    const center = checkLatLon(params)
    if (center) commit('setCenter', center)
    const zoom = checkZoom(params)
    if (zoom) commit('setZoom', zoom)
  },
  setOffset ({ commit }, params) {
    commit('setOffset', params)
  },
  updateFrom ({ commit }, params) {
    commit('setFrom', params)
  },
  recommendSearchCourse({ state, commit, getters }) {
    const places = getters['getRecommendedPlaces']
    const favorited = getters['getFavoritedPlaces']
    const fromLocation = assignFromLocation(state.from.route, state.center)
    const durations = []

    // NOTE: ajaxで返ってきた順になってしまうので、距離順を保つためにindex情報を付加しておく
    places.concat(favorited)
      .forEach((place, index) => {
        const toLocation = place.location

        api.recommendSearchCourse({ fromLocation, toLocation })
          .then((duration) => {
            durations.push({
              index: index + 1,
              place: place,
              amount: duration
            })
          })
      })

    commit('setDurations', durations)
  },
  updateGooglePlaceMarker({ commit }, place) {
    commit('setGooglePlaceMarker', place)
  },
  setSearchFrom ({ commit }, from) {
    commit('setSearchFrom', from)
  },
  setSearchTo ({ commit }, to) {
    commit('setSearchTo', to)
  },
  clearSearchFrom ({ commit }) {
    commit('clearSearchFrom')
  },
  clearSearchTo ({ commit }) {
    commit('clearSearchTo')
  },
  updateSpotMarkerList ({ commit }, spotMarkerList) {
    commit('updateSpotMarkerList', spotMarkerList)
  },
  reset ({ commit }) {
    commit('resetState')
  },
  updateZoom ({ commit }, params) {
    commit('setZoom', params)
  },
  updateHeight ({ commit }, height) {
    commit('setHeight', height)
  }
}

const mutations = {
  setCenter (state, params) {
    state.center = params
  },
  setOffset (state, params) {
    state.offset = params
  },
  setFrom (state, params) {
    state.from = params
  },
  setDurations (state, durations) {
    state.durations = durations
  },
  resetDurations (state) {
    state.durations = []
  },
  setGooglePlaceMarker(state, place) {
    state.googlePlaceMarker = place
  },
  setSearchFrom (state, from) {
    state.searchFrom = from
  },
  setSearchTo (state, to) {
    state.searchTo = to
  },
  clearSearchFrom (state) {
    state.searchFrom = {}
  },
  clearSearchTo (state) {
    state.searchTo = {}
  },
  updateSpotMarkerList (state, spotMarkerList) {
    state.spotMarkerList = spotMarkerList
  },
  resetState (state) {
    Object.assign(state, getDefaultState())
  },
  setZoom (state, params) {
    state.zoom = params
  },
  setHeight (state, height) {
    state.height = height
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}

/**
 * Private functions
 */

function isMatch(place, word) {
  const hiragana = toHiragana(word)
  const katakana = toKatakana(word)

  return isPlaceMatch(place, hiragana, katakana)
    || isGenreWordsMatch(place, hiragana, katakana)
}

function isPlaceMatch(place, hiragana, katakana) {
  const attributes = ['detail', 'name', 'name_yomi']
  const match = attributes.filter(a => {
    return place[a] && (place[a].indexOf(hiragana) > -1 || place[a].indexOf(katakana) > -1)
  })

  return match.length > 0
}

function isGenreWordsMatch(place, hiragana, katakana) {
  const genreWords = place.genreWords.join(',')

  return genreWords.indexOf(hiragana) > -1 ||
    genreWords.indexOf(katakana) > -1
}

function assignFromLocation(route, center) {
  let location = {
    latitude: center.lat,
    longitude: center.lng
  }

  if (route) {
    const point = route.points[0]
    location = point.location
  }

  return location
}

function checkLatLon(params) {
  const mapParams = params.split(',')
  if (mapParams.length < 2) {
    return false
  }
  if ((isNaN(mapParams[0])) || (isNaN(mapParams[1])) )  {
    return false
  }
  const center = {
    lat: parseFloat(mapParams[0]),
    lng: parseFloat(mapParams[1])
  }
  return center
}

function checkZoom(params) {
  const mapParams = params.split(',')
  if (mapParams.length < 3) {
    return false
  }
  const z = mapParams[2]
  const regexp = /^\d{1,2}z$/
  if (!regexp.test(z)) {
    return false
  } else{
    return parseInt(z.replace('z',''))
  }

}
